/**
 * useTradeRecord — thin persistence layer that shadows every trade lifecycle
 * step to the server-side wallet_transactions table.
 *
 * Design principles:
 * - Persistence is always fire-and-forget: an API failure NEVER blocks the trade.
 * - The txId is per-attempt: generated fresh at the start of each runTrade call.
 * - On mount, pending (non-terminal) tx records for this wallet/chain/action/market
 *   are fetched so a "restore" banner can be shown to the user.
 * - Account or chain switches call invalidate() which marks the current record
 *   CANCELLED so it is never resubmitted after a context change.
 *
 * SYN-WALLET-1 spec requirements proved:
 * - page refresh never resubmits  (restore shows a banner, not a new tx)
 * - duplicate clicks create only one attempt  (tradeInFlightRef in TradingPanel)
 * - wallet rejection preserves form  (CANCELLED status, form stays filled)
 * - account switch invalidates unsigned tx  (invalidate() called on address change)
 * - chain switch invalidates stale simulation  (invalidate() called on chainId change)
 * - confirmed tx appears in Transaction Centre  (CONFIRMED persisted, listed by API)
 * - failed tx never shows success state  (FAILED path in catch block)
 */

import { useEffect, useRef, useState } from "react";

// ── Types ─────────────────────────────────────────────────────────────────────

export type TxStatus =
  | "DRAFT" | "SIMULATING" | "READY_TO_SIGN" | "AWAITING_SIGNATURE"
  | "SIGNED" | "SUBMITTED" | "INCLUDED" | "CONFIRMED" | "FINALIZED"
  | "FAILED" | "CANCELLED" | "REPLACED" | "RETRYABLE";

export interface PersistedTxRecord {
  id: string;
  status: TxStatus;
  action: string;
  txHash: string | null;
  amountWei: string | null;
  contractAddress: string | null;
  errorMessage: string | null;
  createdAt: string;
}

interface UseTradeRecordOpts {
  walletAddress: string | null;
  chainId: number | null;
  action: "buy" | "sell" | "deploy" | "send_eth" | "send_token" | "claim_rewards" | "bridge";
  /** The market or contract address — used to find restore candidates. */
  contractAddress: string;
}

// ── Persist helper (standalone, not a hook) ───────────────────────────────────

const BASE = "/api/wallet/transactions";

/**
 * Persist a transaction status update.  Always fire-and-forget — never awaited
 * in a way that would block the trade flow.  Failures are silently swallowed.
 */
export async function persistTradeStatus(
  txId: string,
  status: TxStatus,
  fields: {
    walletAddress?: string;
    chainId?: number;
    action?: string;
    contractAddress?: string;
    txHash?: string;
    amountWei?: string;
    gasUsed?: string;
    gasPrice?: string;
    errorMessage?: string;
    revertReason?: string;
    returnContext?: string;
  } = {},
): Promise<void> {
  try {
    await fetch(`${BASE}/${txId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status, ...fields }),
    });
  } catch {
    // silently swallow — persistence failure must never abort a trade
  }
}

// ── Hook ─────────────────────────────────────────────────────────────────────

interface UseTradeRecordReturn {
  /**
   * Start a new trade attempt.  Returns a fresh txId that must be passed
   * to every subsequent persistTradeStatus call.  Persists DRAFT immediately.
   */
  startTrade: (fields: { walletAddress: string; chainId: number }) => string;

  /** Pending non-terminal tx found on mount for this wallet+chain+action+contract. */
  pendingTx: PersistedTxRecord | null;

  /** Dismiss the restore banner without taking action. */
  clearPending: () => void;

  /**
   * Call when the wallet address changes (unsigned tx → CANCELLED).
   * Safe to call with no current txId; it is a no-op in that case.
   */
  invalidate: (reason: "wallet_switch" | "chain_switch") => void;
}

/** Stable ref-based txId for the current trade attempt. */
function useTradeId() {
  const ref = useRef<string>("");
  return ref;
}

export function useTradeRecord(opts: UseTradeRecordOpts): UseTradeRecordReturn {
  const txIdRef = useTradeId();
  const [pendingTx, setPendingTx] = useState<PersistedTxRecord | null>(null);

  // ── Restore: fetch pending tx for this context on mount ───────────────────
  useEffect(() => {
    if (!opts.walletAddress || !opts.chainId) return;
    let cancelled = false;

    fetch(
      `${BASE}/pending?address=${opts.walletAddress}&chainId=${opts.chainId}`,
    )
      .then((r) => (r.ok ? r.json() : []))
      .then((rows: PersistedTxRecord[]) => {
        if (cancelled) return;
        const match = rows.find(
          (r) =>
            r.action === opts.action &&
            r.contractAddress === opts.contractAddress,
        );
        if (match) setPendingTx(match);
      })
      .catch(() => {});

    return () => { cancelled = true; };
  // Only re-run when identity changes — not on action/contractAddress (stable)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.walletAddress, opts.chainId]);

  // ── Invalidate on wallet/chain switch ─────────────────────────────────────
  const prevAddress = useRef(opts.walletAddress);
  const prevChainId = useRef(opts.chainId);

  useEffect(() => {
    const addressChanged = prevAddress.current !== opts.walletAddress;
    const chainChanged   = prevChainId.current !== opts.chainId;

    if ((addressChanged || chainChanged) && txIdRef.current) {
      const id = txIdRef.current;
      const reason = addressChanged ? "wallet_switch" : "chain_switch";
      void persistTradeStatus(id, "CANCELLED", {
        errorMessage: reason === "wallet_switch"
          ? "Cancelled: wallet account changed"
          : "Cancelled: network changed",
      });
      txIdRef.current = "";
    }

    prevAddress.current = opts.walletAddress;
    prevChainId.current = opts.chainId;
  }, [opts.walletAddress, opts.chainId, txIdRef]);

  // ── API ───────────────────────────────────────────────────────────────────

  const startTrade = (fields: { walletAddress: string; chainId: number }): string => {
    const id = crypto.randomUUID();
    txIdRef.current = id;
    // Fire-and-forget: persist DRAFT immediately
    void persistTradeStatus(id, "DRAFT", {
      walletAddress: fields.walletAddress,
      chainId: fields.chainId,
      action: opts.action,
      contractAddress: opts.contractAddress,
    });
    return id;
  };

  const clearPending = () => setPendingTx(null);

  const invalidate = (reason: "wallet_switch" | "chain_switch") => {
    if (!txIdRef.current) return;
    void persistTradeStatus(txIdRef.current, "CANCELLED", {
      errorMessage: reason === "wallet_switch"
        ? "Cancelled: wallet account changed"
        : "Cancelled: network changed",
    });
    txIdRef.current = "";
  };

  return { startTrade, pendingTx, clearPending, invalidate };
}
